import {
	PUBLIC_DIFFUSION_API_ENDPOINT,
	PUBLIC_DIFFUSION_API_WEBSOCKET_ENDPOINT
} from '$env/static/public';
import { delay } from '$lib/delay';
import {
	Client as DiffusionApiClient,
	type CreateDiffusionJobInfoResponse,
	type DiffusionJobImage,
	type DiffusionJobInfoCreate
} from '$lib/diffusionApi';
import { blur } from './canvasUtilities';
import { isImageGenerationModel } from './imageGenerationModels';

export class Diffuser {
	private job?: DiffusionJobInfoCreate;
	private diffusionApiClient: DiffusionApiClient;
	private token: string = '';
	private locked: boolean = false;
	private tempCanvas: HTMLCanvasElement | undefined = undefined;
	private currentWebSocket?: WebSocket;
	public estimatedWaitTime: number | undefined = undefined;
	public variationCount: number = 1;

	constructor(token: string) {
		this.diffusionApiClient = new DiffusionApiClient({
			BASE: PUBLIC_DIFFUSION_API_ENDPOINT,
			TOKEN: token
		});
		this.token = token;
	}

	public setToken(token: string) {
		this.diffusionApiClient = new DiffusionApiClient({
			BASE: PUBLIC_DIFFUSION_API_ENDPOINT,
			TOKEN: token
		});
		this.token = token;
	}

	public async streamDiffusionJobImages(
		onImage: (image: DiffusionJobImage) => Promise<void>,
		onClose: () => Promise<void>,
		onError: (error: any) => Promise<void>
	) {
		if (!this.job) {
			return;
		}

		let retryDelay: number = 250; // 0.25s
		const maxRetryDelay: number = 8000; // 8s

		let ws: WebSocket | undefined;
		while (retryDelay <= maxRetryDelay) {
			try {
				ws = new WebSocket(
					`${PUBLIC_DIFFUSION_API_WEBSOCKET_ENDPOINT}/diffusion-job-image-stream/${this.job.job_id}?token=${this.token}`
				);
				break;
			} catch (e: any) {
				console.error('Failed to create websocket connection. Retrying...', e);
				retryDelay *= 2; // exponential backoff
				await delay(retryDelay);
			}
		}

		if (!ws) {
			throw new Error('Failed to create initial websocket connection after all retries.');
		}

		this.currentWebSocket = ws;

		let receivedFirstMessage = false;

		ws.onmessage = async (event) => {
			// // ignore the first message, since it could be the
			// // previous job's last image due to a comfyUI bug
			// if (!receivedFirstMessage) {
			// 	receivedFirstMessage = true;
			// 	return;
			// }

			const diffusionJobImage = (() => {
				try {
					return JSON.parse(event.data) as DiffusionJobImage;
				} catch (e: any) {
					return undefined;
				}
			})();

			if (diffusionJobImage === undefined) {
				console.log('Received non-JSON message', event.data);
				return;
			}

			if (this.tempCanvas === undefined) {
				this.tempCanvas = document.createElement('canvas');
			}

			let imageSrc: string | undefined = undefined;
			if (diffusionJobImage.image_encoding === 'png') {
				imageSrc = `data:image/png;base64,${diffusionJobImage.image_src}`;
			} else {
				imageSrc = `data:image/jpeg;base64,${diffusionJobImage.image_src}`;
				imageSrc = await blur(imageSrc, 0.6, this.tempCanvas);
			}
			diffusionJobImage.image_src = imageSrc;
			await onImage(diffusionJobImage);
		};
		ws.onclose = async () => {
			await onClose();
		};
		ws.onerror = async (error) => {
			await onError(error);
		};
	}

	public setJob(job: DiffusionJobInfoCreate) {
		this.job = job;
	}

	public cancelJob() {
		if (this.currentWebSocket) {
			this.currentWebSocket.close();
			this.currentWebSocket = undefined;
		}
	}

	public async createDiffusionJob(): Promise<void | { status: number }> {
		this.estimatedWaitTime = undefined;

		let retryDelay: number = 250; // 0.25s
		const maxRetryDelay: number = 4000; // 4s

		while (this.locked) {
			await delay(10);
		}
		this.locked = true;

		try {
			this.locked = true;
			if (!this.job) {
				return;
			}
			if (!isImageGenerationModel(this.job.model_name)) {
				throw new Error('Choose Aeolian 2 or GPT before generating.');
			}
			console.log('creating diffusion job');
			const t0 = performance.now();

			while (retryDelay <= maxRetryDelay) {
				try {
					const response =
						await this.diffusionApiClient.default.createDiffusionJobDiffusionJobInfoPost({
							job: this.job
						});
					const t1 = performance.now();
					console.log('diffusion job created in', t1 - t0, 'ms');
					this.estimatedWaitTime = response.estimated_wait_time ?? undefined;
					this.variationCount = response.variation_count ?? 1;
					break; // success, exit the retry loop
				} catch (e: any) {
					if (e.status === 403) {
						return e; // return forbiddens with no retry
					}
					if (e.status === 400) {
						return e; // also return bad requests with no retry
					}
					console.log(
						`Failed to call createDiffusionJob. Retrying in ${retryDelay / 1000} seconds...`
					);
					await delay(retryDelay);
					retryDelay *= 2; // exponential backoff
				}
			}

			if (retryDelay > maxRetryDelay) {
				throw new Error('Max retries reached, failed to create diffusion job.');
			}
		} catch (e: any) {
			return e;
		} finally {
			this.locked = false;
		}
	}
}
